import { UserDetailsQuery } from '@/__generated__/graphql';
import { AbyssIcon, AbyssIcons } from '@/assets/icons/AbyssIcon';
import { NOT_APPLICABLE_LABEL, UserTypes } from '@/constants';
import { upscaleGoogleContentImage } from '@/utils';
import { Avatar, Box, Stack, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { ProfileButton } from '../styles';
import { ResetPasswordDialog } from './ResetPasswordDialog';
import { UserProfileContainer } from './common';
import { Link } from 'react-router-dom';

type TitleValuePairProps = {
  title: string;
  value?: string;
  link?: {
    href: string;
    title: string;
  };
  button?: {
    clickHandler: () => void;
    label: string;
    icon?: AbyssIcons;
    disabled?: boolean;
  };
};
const TitleValuePair = ({ title, value, link, button }: TitleValuePairProps) => {
  return (
    <Stack gap={button ? '0.2rem' : '0.8rem'} width="fit-content">
      <Typography fontSize="1.6rem" fontWeight={500}>
        {title}
      </Typography>
      {link && (
        <Link
          style={{
            color: '165DFF',
            textDecoration: 'none',
            fontSize: '1.6rem',
          }}
          to={link.href}
        >
          {link.title}
        </Link>
      )}
      {button && (
        <ProfileButton contained onClick={button.clickHandler} disabled={!!button.disabled}>
          {button.icon && <AbyssIcon iconName={button.icon} style={{ width: '1.6rem' }} />}
          <span>{button.label}</span>
        </ProfileButton>
      )}
      {value && (
        <Typography fontSize="1.6rem" fontWeight={400} color="#50739B">
          {value}
        </Typography>
      )}
    </Stack>
  );
};

type Props = {
  user: UserDetailsQuery['userDetails']['user'];
};

const getUserType = (type: string) => {
  switch (type) {
    case 'BP-Azure-AD':
      return UserTypes.SSO;
    case 'google-oauth2':
      return UserTypes.SSO;
    default:
      return UserTypes.Local;
  }
};

export const UserDetails = ({ user }: Props) => {
  const [showResetDialog, setShowResetDialog] = useState<boolean>(false);
  const dataItems: TitleValuePairProps[] = useMemo(() => {
    return [
      { title: 'Name', value: user.name || NOT_APPLICABLE_LABEL },
      {
        title: 'Reset password',
        button: {
          clickHandler: () => {
            setShowResetDialog(true);
          },
          label: 'Send link',
          icon: 'send',
          disabled: getUserType(user.connection ?? '') !== UserTypes.Local,
        },
      },
      { title: 'Department', value: NOT_APPLICABLE_LABEL },
      { title: 'Email', value: user.email || NOT_APPLICABLE_LABEL },
      { title: 'Company', value: NOT_APPLICABLE_LABEL },
      { title: 'Designation', value: NOT_APPLICABLE_LABEL },
      { title: 'User type', value: getUserType(user.connection ?? '') },
      { title: 'Location', value: NOT_APPLICABLE_LABEL },
      { title: 'Functions', value: NOT_APPLICABLE_LABEL },
    ];
  }, [user.connection, user.email, user.name]);

  return (
    <UserProfileContainer>
      <ResetPasswordDialog
        email={user.email}
        loginCount={user.loginCount}
        connection={user.connection}
        open={showResetDialog}
        handleClose={() => {
          setShowResetDialog(false);
        }}
      />
      <Box sx={{ flex: 1 }}>
        <Typography fontSize={16} fontWeight={700}>
          User Details
        </Typography>
        <Box
          sx={{
            mt: 2,
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 2,
          }}
        >
          {dataItems.map((item) => {
            return <TitleValuePair {...item} key={item.value} />;
          })}
        </Box>
      </Box>
      <Stack direction="row" justifyContent="center" alignItems="center" sx={{ width: '20%' }}>
        <Avatar
          sx={{
            height: '100%',
            width: '100%',
            aspectRatio: '1 / 1',
            maxWidth: '22.4rem',
            maxHeight: '22.4rem',
          }}
          src={user?.picture ? upscaleGoogleContentImage(user?.picture) : ''}
          imgProps={{
            crossOrigin: 'anonymous',
          }}
        />
      </Stack>
    </UserProfileContainer>
  );
};
